import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import Toast from "../../components/Toast";
import { canAccessHedgePlanning, getLoginDetails } from "../../utils/storage";
import {
  METALS,
  SAMPLE_HEDGES,
  SAMPLE_LOTS,
  SAMPLE_SETTLEMENTS,
  allocateFifo,
  applySettlementsToLots,
  convertersForLot,
  gapOz,
  ozValue,
  rankConverters,
  rankInvoices,
} from "./lotHedgeSettlementSample";

import "./insights.css";
import "./lot-hedge-settlement.css";

function formatOz(n) {
  if (n == null || Number.isNaN(Number(n))) return "—";
  return Number(n).toFixed(2);
}

function formatUsd(n) {
  if (n == null || Number.isNaN(Number(n))) return "—";
  const abs = Math.abs(n);
  const formatted = abs.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });
  if (n < 0) return `−${formatted}`;
  return formatted;
}

function statusLabel(status) {
  switch (status) {
    case "open":
      return "Open";
    case "awaiting-settlement":
      return "Awaiting settlement";
    case "settled":
      return "Settled";
    case "variance":
      return "Variance";
    default:
      return status;
  }
}

function gapClass(n) {
  if (n == null || Math.abs(n) < 0.01) return "";
  return n > 0 ? " is-neg" : " is-pos";
}

function buildPartnerScorecard(lots, hedges) {
  const byPartner = {};
  hedges.forEach((h) => {
    byPartner[h.partner] = {
      partner: h.partner,
      lots: 0,
      settled: 0,
      varianceLots: 0,
      awaiting: 0,
      ourValue: 0,
      settledValue: 0,
    };
  });
  lots.forEach((lot) => {
    const partners = [...new Set((lot.hedgeSplits || []).map((s) => s.partner))];
    const prices = (lot.hedgeSplits || [])[0]?.prices || { pt: 980, pd: 980, rh: 4850 };
    partners.forEach((partner) => {
      if (!byPartner[partner]) {
        byPartner[partner] = {
          partner,
          lots: 0,
          settled: 0,
          varianceLots: 0,
          awaiting: 0,
          ourValue: 0,
          settledValue: 0,
        };
      }
      const row = byPartner[partner];
      row.lots += 1;
      row.ourValue += ozValue(lot.ourOz, prices);
      if (lot.settlementOz) {
        row.settled += 1;
        row.settledValue += ozValue(lot.settlementOz, prices);
      }
      if (lot.status === "variance") row.varianceLots += 1;
      if (lot.status === "awaiting-settlement") row.awaiting += 1;
    });
  });
  return Object.values(byPartner)
    .map((row) => {
      const cashGap = row.ourValue - row.settledValue;
      const settleRate = row.lots > 0 ? (row.settled / row.lots) * 100 : 0;
      const varianceRate = row.lots > 0 ? (row.varianceLots / row.lots) * 100 : 0;
      let grade = "A";
      if (varianceRate >= 40) grade = "C";
      else if (varianceRate >= 20 || settleRate < 50) grade = "B";
      return { ...row, cashGap, settleRate, varianceRate, grade };
    })
    .sort((a, b) => b.cashGap - a.cashGap);
}

const TABS = [
  { id: "lots", label: "Lots" },
  { id: "fifo", label: "FIFO hedges" },
  { id: "settlements", label: "Settlements" },
  { id: "converters", label: "Converter suspects" },
];

const LotHedgeSettlementV1 = () => {
  const loginDetails = getLoginDetails();
  const canView = canAccessHedgePlanning(loginDetails);

  const [tab, setTab] = useState("lots");
  const [settlements, setSettlements] = useState(SAMPLE_SETTLEMENTS);
  const [selectedLotId, setSelectedLotId] = useState(SAMPLE_LOTS[2].lotId);
  const [statusFilter, setStatusFilter] = useState("all");
  const [uploadForm, setUploadForm] = useState({ pt: "", pd: "", rh: "" });
  const [toastParams, setToastParams] = useState({ message: "", type: "", id: 0 });

  const lots = useMemo(
    () => applySettlementsToLots(SAMPLE_LOTS, settlements),
    [settlements]
  );

  const fifo = useMemo(() => allocateFifo(SAMPLE_HEDGES, lots), [lots]);

  const lotsWithSplits = useMemo(
    () =>
      lots.map((lot) => ({
        ...lot,
        hedgeSplits: fifo.lotSplits[lot.lotId] || [],
      })),
    [lots, fifo]
  );

  const selectedLot =
    lotsWithSplits.find((l) => l.lotId === selectedLotId) || lotsWithSplits[0] || null;

  const filteredLots = useMemo(() => {
    if (statusFilter === "all") return lotsWithSplits;
    return lotsWithSplits.filter((l) => l.status === statusFilter);
  }, [lotsWithSplits, statusFilter]);

  const summary = useMemo(() => {
    const awaiting = lotsWithSplits.filter((l) => l.status === "awaiting-settlement").length;
    const settled = lotsWithSplits.filter((l) => l.status === "settled").length;
    const withVariance = lotsWithSplits.filter((l) => l.status === "variance").length;
    const open = lotsWithSplits.filter((l) => l.status === "open").length;
    return { awaiting, settled, withVariance, open, total: lotsWithSplits.length };
  }, [lotsWithSplits]);

  const cashGap = useMemo(() => {
    let expected = 0;
    let settled = 0;
    let openExposure = 0;
    lotsWithSplits.forEach((lot) => {
      const prices = lot.hedgeSplits[0]?.prices || { pt: 980, pd: 980, rh: 4850 };
      const ourVal = ozValue(lot.ourOz, prices);
      expected += ourVal;
      if (lot.settlementOz) settled += ozValue(lot.settlementOz, prices);
      else openExposure += ourVal;
    });
    return { expected, settled, gap: expected - settled, openExposure };
  }, [lotsWithSplits]);

  const partnerScorecard = useMemo(
    () => buildPartnerScorecard(lotsWithSplits, SAMPLE_HEDGES),
    [lotsWithSplits]
  );

  const invoiceRanks = useMemo(
    () => (selectedLot ? rankInvoices(selectedLot) : []),
    [selectedLot]
  );

  const lotConverters = useMemo(
    () => (selectedLot ? convertersForLot(selectedLot) : []),
    [selectedLot]
  );

  const allConverterSuspects = useMemo(() => rankConverters(lotsWithSplits), [lotsWithSplits]);

  const selectedCash = useMemo(() => {
    if (!selectedLot) return null;
    const prices = selectedLot.hedgeSplits[0]?.prices || { pt: 980, pd: 980, rh: 4850 };
    const ourValue = ozValue(selectedLot.ourOz, prices);
    const settledValue = selectedLot.settlementOz
      ? ozValue(selectedLot.settlementOz, prices)
      : null;
    const gap = settledValue != null ? ourValue - settledValue : null;
    return { ourValue, settledValue, gap, prices };
  }, [selectedLot]);

  useEffect(() => {
    if (!canView) window.location.href = "/";
  }, [canView]);

  if (!canView) return null;

  const handleSelectLot = (lotId) => {
    setSelectedLotId(lotId);
    setUploadForm({ pt: "", pd: "", rh: "" });
    setTab("lots");
  };

  const handleUploadSettlement = (e) => {
    e.preventDefault();
    if (!selectedLot) return;
    const pt = parseFloat(uploadForm.pt);
    const pd = parseFloat(uploadForm.pd);
    const rh = parseFloat(uploadForm.rh);
    if ([pt, pd, rh].some((v) => Number.isNaN(v))) {
      setToastParams({
        type: "ERROR",
        message: "Enter valid Pt / Pd / Rh settlement ounces for this lot.",
        id: Date.now(),
      });
      return;
    }

    setSettlements((prev) => {
      const next = prev.map((s) => ({ ...s, lines: s.lines.map((l) => ({ ...l })) }));
      let found = false;
      next.forEach((s) => {
        s.lines = s.lines.map((line) => {
          if (line.lotId === selectedLot.lotId) {
            found = true;
            return { ...line, pt, pd, rh };
          }
          return line;
        });
      });
      if (!found) {
        next.push({
          id: `SET-MANUAL-${Date.now()}`,
          partner: selectedLot.hedgeSplits[0]?.partner || "Partner",
          date: new Date().toISOString().slice(0, 10),
          lines: [{ lotId: selectedLot.lotId, pt, pd, rh }],
        });
      }
      return next;
    });
    setUploadForm({ pt: "", pd: "", rh: "" });
    setToastParams({
      type: "SUCCESS",
      message: `Settlement ounces applied to ${selectedLot.lotId} (demo — not saved)`,
      id: Date.now(),
    });
  };

  return (
    <>
      <div className="lot-hedge-page">
        <div className="lot-hedge-header lot-hedge-header--compact">
          <Link to="/hedge" className="lot-hedge-link-btn">
            Hedge contracts
          </Link>
        </div>

        <div className="lot-hedge-summary">
          <div className="lot-hedge-summary-card">
            <div className="lot-hedge-summary-label">Ceramic lots</div>
            <div className="lot-hedge-summary-value">{summary.total}</div>
          </div>
          <div className="lot-hedge-summary-card">
            <div className="lot-hedge-summary-label">Open</div>
            <div className="lot-hedge-summary-value">{summary.open}</div>
          </div>
          <div className="lot-hedge-summary-card lot-hedge-summary-card--warn">
            <div className="lot-hedge-summary-label">Awaiting settlement</div>
            <div className="lot-hedge-summary-value">{summary.awaiting}</div>
          </div>
          <div className="lot-hedge-summary-card lot-hedge-summary-card--ok">
            <div className="lot-hedge-summary-label">Settled clean</div>
            <div className="lot-hedge-summary-value">{summary.settled}</div>
          </div>
          <div className="lot-hedge-summary-card lot-hedge-summary-card--risk">
            <div className="lot-hedge-summary-label">With variance</div>
            <div className="lot-hedge-summary-value">{summary.withVariance}</div>
          </div>
        </div>

        <div className="lot-hedge-exec-row">
          <div className="lot-hedge-cash-card">
            <div className="lot-hedge-exec-title">Cash gap (at hedge prices)</div>
            <p className="lot-hedge-exec-hint">
              Invoice assay ounces vs partner-settled ounces, valued at the hedge that received
              the lot.
            </p>
            <div className="lot-hedge-cash-grid">
              <div>
                <div className="lot-hedge-summary-label">Expected (ours)</div>
                <div className="lot-hedge-cash-value">{formatUsd(cashGap.expected)}</div>
              </div>
              <div>
                <div className="lot-hedge-summary-label">Settled by partners</div>
                <div className="lot-hedge-cash-value lot-hedge-cash-value--ok">
                  {formatUsd(cashGap.settled)}
                </div>
              </div>
              <div>
                <div className="lot-hedge-summary-label">Open cash gap</div>
                <div
                  className={`lot-hedge-cash-value${
                    cashGap.gap > 0 ? " lot-hedge-cash-value--risk" : " lot-hedge-cash-value--ok"
                  }`}
                >
                  {formatUsd(cashGap.gap)}
                </div>
              </div>
              <div>
                <div className="lot-hedge-summary-label">Unsettled exposure</div>
                <div className="lot-hedge-cash-value lot-hedge-cash-value--warn">
                  {formatUsd(cashGap.openExposure)}
                </div>
              </div>
            </div>
          </div>

          <div className="lot-hedge-scorecard-card">
            <div className="lot-hedge-exec-title">Partner scorecard</div>
            <p className="lot-hedge-exec-hint">
              Lots that FIFO-allocated to each partner. Variance % is lots with a metal gap.
            </p>
            <table className="lot-hedge-scorecard-table">
              <thead>
                <tr>
                  <th>Partner</th>
                  <th>Lots</th>
                  <th>Settle %</th>
                  <th>Variance %</th>
                  <th>Cash gap</th>
                  <th>Grade</th>
                </tr>
              </thead>
              <tbody>
                {partnerScorecard.map((row) => (
                  <tr key={row.partner}>
                    <td className="lot-hedge-partner-name">{row.partner}</td>
                    <td>{row.lots}</td>
                    <td className="lot-hedge-mono">{row.settleRate.toFixed(0)}%</td>
                    <td className="lot-hedge-mono">{row.varianceRate.toFixed(0)}%</td>
                    <td
                      className={`lot-hedge-mono${
                        row.cashGap > 1000 ? " is-neg" : row.cashGap < -1000 ? " is-pos" : ""
                      }`}
                    >
                      {formatUsd(row.cashGap)}
                    </td>
                    <td>
                      <span className={`lot-hedge-grade lot-hedge-grade--${row.grade.toLowerCase()}`}>
                        {row.grade}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="lot-hedge-filters">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              className={`lot-hedge-filter-chip${tab === t.id ? " is-active" : ""}`}
              onClick={() => setTab(t.id)}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === "fifo" && (
          <div className="lot-hedge-fifo-grid">
            {fifo.hedges.map((h) => (
              <div key={h.id} className="lot-hedge-fifo-card">
                <div className="lot-hedge-fifo-head">
                  <div>
                    <div className="lot-hedge-lot-id">{h.partner}</div>
                    <div className="lot-hedge-lot-meta">
                      {h.id} · {h.date} · {h.status}
                    </div>
                  </div>
                </div>
                {METALS.map((m) => (
                  <div key={m} className="lot-hedge-fill">
                    <div className="lot-hedge-fill-label">
                      <span>{m.toUpperCase()}</span>
                      <span className="lot-hedge-mono">
                        {formatOz(h.filled[m])} / {formatOz(h.committed[m])} · {h.fillPct[m]}%
                      </span>
                    </div>
                    <div className="lot-hedge-fill-track">
                      <div className="lot-hedge-fill-bar" style={{ width: `${Math.min(100, h.fillPct[m])}%` }} />
                    </div>
                  </div>
                ))}
                <div className="lot-hedge-fifo-lots">
                  {(h.splits || []).length === 0 && (
                    <div className="lot-hedge-upload-hint">No lots allocated yet.</div>
                  )}
                  {(h.splits || []).map((s) => (
                    <button
                      key={s.lotId}
                      type="button"
                      className="lot-hedge-split-chip"
                      onClick={() => handleSelectLot(s.lotId)}
                    >
                      {s.lotId}
                      <span>
                        Pt {formatOz(s.oz.pt)} · Pd {formatOz(s.oz.pd)} · Rh {formatOz(s.oz.rh)}
                      </span>
                    </button>
                  ))}
                </div>
                <div className="lot-hedge-lot-meta">
                  Remaining Pt {formatOz(h.remaining.pt)} · Pd {formatOz(h.remaining.pd)} · Rh{" "}
                  {formatOz(h.remaining.rh)}
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "settlements" && (
          <div className="lot-hedge-table-wrap">
            {settlements.map((s) => (
              <div key={s.id} className="lot-hedge-settle-block">
                <div className="lot-hedge-fifo-head">
                  <div>
                    <div className="lot-hedge-lot-id">{s.id}</div>
                    <div className="lot-hedge-lot-meta">
                      {s.partner} · {s.date} · {s.lines.length} lot{s.lines.length === 1 ? "" : "s"}
                    </div>
                  </div>
                </div>
                <table className="lot-hedge-table">
                  <thead>
                    <tr>
                      <th>Lot</th>
                      <th>Our Pt / Pd / Rh</th>
                      <th>Settled Pt / Pd / Rh</th>
                      <th>Gap</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {s.lines.map((line) => {
                      const lot = lotsWithSplits.find((l) => l.lotId === line.lotId);
                      const gap = lot ? gapOz(lot.ourOz, lot.settlementOz) : null;
                      return (
                        <tr key={line.lotId} onClick={() => handleSelectLot(line.lotId)}>
                          <td className="lot-hedge-lot-id">{line.lotId}</td>
                          <td className="lot-hedge-mono">
                            {lot
                              ? `${formatOz(lot.ourOz.pt)} / ${formatOz(lot.ourOz.pd)} / ${formatOz(lot.ourOz.rh)}`
                              : "—"}
                          </td>
                          <td className="lot-hedge-mono">
                            {formatOz(line.pt)} / {formatOz(line.pd)} / {formatOz(line.rh)}
                          </td>
                          <td className={`lot-hedge-mono${gapClass(gap?.pt)}`}>
                            {gap
                              ? `${formatOz(gap.pt)} / ${formatOz(gap.pd)} / ${formatOz(gap.rh)}`
                              : "—"}
                          </td>
                          <td>
                            {lot && (
                              <span className={`lot-hedge-status lot-hedge-status--${lot.status}`}>
                                {statusLabel(lot.status)}
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        )}

        {tab === "converters" && (
          <div className="lot-hedge-table-wrap">
            <div className="lot-hedge-detail" style={{ boxShadow: "none" }}>
              <h3 className="lot-hedge-section-title">Converters to review</h3>
              <p className="lot-hedge-upload-hint">
                Lot-level settlement gap is attributed to converters by their assay-ounce share,
                then boosted when the same OEM shows up in more than one variance lot. High
                confidence rows are the ones to change assays on first. ML can replace this
                attribution later using more history.
              </p>
              <table className="lot-hedge-table">
                <thead>
                  <tr>
                    <th>Converter</th>
                    <th>Assay Pt/Pd/Rh</th>
                    <th>Variance lots</th>
                    <th>Attributed gap</th>
                    <th>Est. $ impact</th>
                    <th>Suggested change</th>
                    <th>Confidence</th>
                  </tr>
                </thead>
                <tbody>
                  {allConverterSuspects.map((c) => (
                    <tr key={c.oem}>
                      <td>
                        <div className="lot-hedge-lot-id">{c.oem}</div>
                        <div className="lot-hedge-lot-meta">
                          {c.type} · {c.qty} units · {c.invoices.length} invoices
                        </div>
                      </td>
                      <td className="lot-hedge-mono">
                        {c.ptAssay} / {c.pdAssay} / {c.rhAssay}
                      </td>
                      <td>
                        {c.varianceLots.length}/{c.lots.length}
                        <div className="lot-hedge-lot-meta">{c.varianceLots.join(", ") || "—"}</div>
                      </td>
                      <td className={`lot-hedge-mono${gapClass(c.attributed.pt)}`}>
                        {formatOz(c.attributed.pt)} / {formatOz(c.attributed.pd)} /{" "}
                        {formatOz(c.attributed.rh)}
                      </td>
                      <td className={`lot-hedge-mono${gapClass(c.cashImpact)}`}>
                        {formatUsd(c.cashImpact)}
                      </td>
                      <td>{c.suggestion}</td>
                      <td>
                        <span className={`lot-hedge-conf lot-hedge-conf--${c.confidence}`}>
                          {c.confidence}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "lots" && (
          <>
            <div className="lot-hedge-filters">
              <span className="lot-hedge-filters-label">Status</span>
              {["all", "open", "awaiting-settlement", "settled", "variance"].map((key) => (
                <button
                  key={key}
                  type="button"
                  className={`lot-hedge-filter-chip${statusFilter === key ? " is-active" : ""}`}
                  onClick={() => setStatusFilter(key)}
                >
                  {key === "all" ? "All" : statusLabel(key)}
                </button>
              ))}
            </div>

            <div className="lot-hedge-layout">
              <div className="lot-hedge-table-wrap">
                <table className="lot-hedge-table">
                  <thead>
                    <tr>
                      <th>Ceramic lot</th>
                      <th>Hedges (FIFO)</th>
                      <th>Invoices</th>
                      <th>Our Pt</th>
                      <th>Our Pd</th>
                      <th>Our Rh</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredLots.map((lot) => (
                      <tr
                        key={lot.lotId}
                        className={selectedLot?.lotId === lot.lotId ? "is-selected" : ""}
                        onClick={() => handleSelectLot(lot.lotId)}
                      >
                        <td>
                          <div className="lot-hedge-lot-id">{lot.lotId}</div>
                          <div className="lot-hedge-lot-meta">
                            {lot.closedDate ? `Closed ${lot.closedDate}` : `Opened ${lot.openedDate}`}
                          </div>
                        </td>
                        <td>
                          {(lot.hedgeSplits || []).length === 0 && "—"}
                          {(lot.hedgeSplits || []).map((s) => (
                            <div key={s.hedgeId} className="lot-hedge-lot-meta">
                              {s.partner} {s.hedgeId}
                            </div>
                          ))}
                        </td>
                        <td>{lot.invoiceCount}</td>
                        <td className="lot-hedge-mono">{formatOz(lot.ourOz.pt)}</td>
                        <td className="lot-hedge-mono">{formatOz(lot.ourOz.pd)}</td>
                        <td className="lot-hedge-mono">{formatOz(lot.ourOz.rh)}</td>
                        <td>
                          <span className={`lot-hedge-status lot-hedge-status--${lot.status}`}>
                            {statusLabel(lot.status)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {selectedLot && (
                <div className="lot-hedge-detail">
                  <div className="lot-hedge-detail-header">
                    <div>
                      <h3 className="lot-hedge-detail-title">{selectedLot.lotId}</h3>
                      <p className="lot-hedge-detail-sub">
                        {(selectedLot.hedgeSplits || [])
                          .map((s) => `${s.partner} ${s.hedgeId}`)
                          .join(" → ") || "Not allocated"}
                        {selectedLot.settlementId ? ` · ${selectedLot.settlementId}` : ""}
                      </p>
                    </div>
                    <span className={`lot-hedge-status lot-hedge-status--${selectedLot.status}`}>
                      {statusLabel(selectedLot.status)}
                    </span>
                  </div>

                  {(selectedLot.hedgeSplits || []).length > 1 && (
                    <div className="lot-hedge-split-note">
                      This lot split across hedges (FIFO leftover).{" "}
                      {selectedLot.hedgeSplits.map((s) => (
                        <span key={s.hedgeId}>
                          {s.partner}: Pt {formatOz(s.oz.pt)}{" "}
                        </span>
                      ))}
                    </div>
                  )}

                  <div className="lot-hedge-compare">
                    <div className="lot-hedge-compare-col">
                      <div className="lot-hedge-compare-label">Our ounces (invoice assays)</div>
                      {METALS.map((m) => (
                        <div key={m} className="lot-hedge-compare-row">
                          <span>{m.toUpperCase()}</span>
                          <strong className="lot-hedge-mono">{formatOz(selectedLot.ourOz[m])}</strong>
                        </div>
                      ))}
                      {selectedCash && (
                        <div className="lot-hedge-compare-meta">
                          Value {formatUsd(selectedCash.ourValue)}
                        </div>
                      )}
                    </div>
                    <div className="lot-hedge-compare-col">
                      <div className="lot-hedge-compare-label">Partner settlement (this lot)</div>
                      {METALS.map((m) => (
                        <div key={m} className="lot-hedge-compare-row">
                          <span>{m.toUpperCase()}</span>
                          <strong className="lot-hedge-mono">
                            {formatOz(selectedLot.settlementOz?.[m])}
                          </strong>
                        </div>
                      ))}
                      {selectedLot.settlementDate && (
                        <div className="lot-hedge-compare-meta">
                          {selectedLot.settlementPartner} · {selectedLot.settlementDate}
                          {selectedCash?.settledValue != null
                            ? ` · ${formatUsd(selectedCash.settledValue)}`
                            : ""}
                        </div>
                      )}
                    </div>
                    <div className="lot-hedge-compare-col lot-hedge-compare-col--gap">
                      <div className="lot-hedge-compare-label">Gap (ours − settlement)</div>
                      {METALS.map((m) => {
                        const gap = gapOz(selectedLot.ourOz, selectedLot.settlementOz);
                        return (
                          <div key={m} className="lot-hedge-compare-row">
                            <span>{m.toUpperCase()}</span>
                            <strong className={`lot-hedge-mono${gapClass(gap?.[m])}`}>
                              {gap ? formatOz(gap[m]) : "—"}
                            </strong>
                          </div>
                        );
                      })}
                      {selectedCash?.gap != null && (
                        <div
                          className={`lot-hedge-compare-meta lot-hedge-cash-gap-line${gapClass(
                            selectedCash.gap
                          )}`}
                        >
                          Cash gap {formatUsd(selectedCash.gap)}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="lot-hedge-upload">
                    <h4 className="lot-hedge-section-title">Apply settlement ounces for this lot</h4>
                    <p className="lot-hedge-upload-hint">
                      Partner files list ounces per lot (a file can contain several lots). Demo
                      only — not persisted.
                    </p>
                    <form className="lot-hedge-upload-form" onSubmit={handleUploadSettlement}>
                      {METALS.map((m) => (
                        <label key={m}>
                          {m.toUpperCase()} oz
                          <input
                            type="number"
                            step="0.01"
                            value={uploadForm[m]}
                            onChange={(e) => setUploadForm({ ...uploadForm, [m]: e.target.value })}
                            placeholder={formatOz(selectedLot.ourOz[m])}
                          />
                        </label>
                      ))}
                      <button type="submit" className="lot-hedge-upload-btn">
                        Apply to lot
                      </button>
                    </form>
                  </div>

                  <div className="lot-hedge-invoices">
                    <h4 className="lot-hedge-section-title">
                      Invoices in this lot ({selectedLot.invoiceCount})
                    </h4>
                    <p className="lot-hedge-upload-hint">
                      Gap is spread by each invoice&apos;s share of lot assay ounces. Highest
                      attributed gap first.
                    </p>
                    <table className="lot-hedge-invoice-table">
                      <thead>
                        <tr>
                          <th>Invoice</th>
                          <th>Customer</th>
                          <th>Pt / Pd / Rh</th>
                          <th>Attributed gap</th>
                        </tr>
                      </thead>
                      <tbody>
                        {invoiceRanks.map((inv) => (
                          <tr key={inv.loadId}>
                            <td>{inv.loadId}</td>
                            <td>
                              {inv.company}
                              <div className="lot-hedge-lot-meta">{inv.date}</div>
                            </td>
                            <td className="lot-hedge-mono">
                              {formatOz(inv.pt)} / {formatOz(inv.pd)} / {formatOz(inv.rh)}
                            </td>
                            <td className={`lot-hedge-mono${gapClass(inv.attributed.pt)}`}>
                              {formatOz(inv.attributed.pt)} / {formatOz(inv.attributed.pd)} /{" "}
                              {formatOz(inv.attributed.rh)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="lot-hedge-invoices" style={{ marginTop: 16 }}>
                    <h4 className="lot-hedge-section-title">Converters likely causing the gap</h4>
                    <p className="lot-hedge-upload-hint">
                      Same attribution at OEM level. Repeat offenders across lots show on the
                      Converter suspects tab.
                    </p>
                    <table className="lot-hedge-invoice-table">
                      <thead>
                        <tr>
                          <th>OEM</th>
                          <th>Assay</th>
                          <th>Our oz</th>
                          <th>Attributed gap</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {lotConverters.slice(0, 6).map((c) => (
                          <tr key={c.oem}>
                            <td>
                              <div className="lot-hedge-lot-id">{c.oem}</div>
                              <div className="lot-hedge-lot-meta">
                                {c.qty} pcs · {c.invoices.length} invoices
                              </div>
                            </td>
                            <td className="lot-hedge-mono">
                              {c.ptAssay}/{c.pdAssay}/{c.rhAssay}
                            </td>
                            <td className="lot-hedge-mono">
                              {formatOz(c.ourOz.pt)} / {formatOz(c.ourOz.pd)} / {formatOz(c.ourOz.rh)}
                            </td>
                            <td className={`lot-hedge-mono${gapClass(c.attributed.pt)}`}>
                              {formatOz(c.attributed.pt)} / {formatOz(c.attributed.pd)} /{" "}
                              {formatOz(c.attributed.rh)}
                            </td>
                            <td>{c.suggestion}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {toastParams.message && (
        <Toast type={toastParams.type} message={toastParams.message} id={toastParams.id} />
      )}
    </>
  );
};

export default LotHedgeSettlementV1;
